import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { makeRequest } from "../../../services/apiRequests";
import { getAuthConfig } from "../../../services/apiUtils";
import { usePermission } from "../../../context/PermissionContext";
import "./NewsroomControlCenter.css";

const LABELS = {
  standard_news: "News Update",
  release_preview: "Upcoming Release",
  song_review: "Song Review",
  album_review: "Album Review",
  music_story: "Music Story",
  artist_spotlight: "Artist Spotlight",
  industry_analysis: "Industry Analysis",
};

const STATUS_LABELS = {
  queued: "Queued",
  researching: "Researching",
  verifying_media: "Verifying media",
  writing: "Writing",
  validating: "Validating",
  draft_created: "Draft ready",
  blocked: "Blocked",
  failed: "Technical failure",
  cancelled: "Cancelled",
};

const TARGETED_TYPES = ["song_review", "album_review"];

function commandKey(action) {
  return `newsroom-ui:${action}:${Date.now()}:${Math.random().toString(36).slice(2, 10)}`;
}

function splitLines(value, limit) {
  return Array.from(
    new Set(
      String(value || "")
        .split(/\n|,/)
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ).slice(0, limit);
}

function compactPayload(payload) {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => {
      if (Array.isArray(value)) return value.length > 0;
      return value !== undefined && value !== null && value !== "";
    }),
  );
}

function normalizeCatalogRows(response) {
  const data = response?.data ?? response;
  if (Array.isArray(data)) return data;
  return data?.items || data?.results || data?.songs || data?.albums || [];
}

function catalogId(item) {
  return item?._id || item?.id || item?.songId || item?.song_id || item?.albumId || item?.album_id || "";
}

function catalogTitle(item) {
  return item?.title || item?.song_name || item?.songName || item?.albumName || item?.album_name || item?.name || "Untitled";
}

function catalogArtist(item) {
  return item?.artist || item?.artist_name || item?.artistName || item?.primaryArtist || item?.artists?.[0]?.artist_name || item?.artists?.[0]?.name || "";
}

function wordCount(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;
}

function listText(items) {
  return Array.isArray(items) && items.length ? items.join(", ") : "None";
}

async function searchCatalog(payload, config = {}) {
  const query = new URLSearchParams();
  Object.entries(payload).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") query.set(key, value);
  });
  const { data, status } = await makeRequest(
    "GET",
    `/admin/newsroom/catalog-search?${query.toString()}`,
    {},
    config,
  );
  return status === 200 ? data : null;
}

async function dryRunNewsroom(payload, config = {}) {
  const { data, status } = await makeRequest(
    "POST",
    "/admin/newsroom/dry-run",
    payload,
    config,
  );
  return status >= 200 && status < 300 ? data : null;
}

function NewsroomControlCenter() {
  const navigate = useNavigate();
  const { hasPermission, isSuperAdmin } = usePermission();
  const canConfigure = isSuperAdmin() || hasPermission("news:approve");
  const canEdit = isSuperAdmin() || hasPermission("news:edit");
  const canCreate = isSuperAdmin() || hasPermission("news:create");
  const canReview = isSuperAdmin() || hasPermission("news:approve");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [capabilities, setCapabilities] = useState(null);
  const [control, setControl] = useState(null);
  const [runs, setRuns] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 25, total: 0, pages: 0 });
  const [filters, setFilters] = useState({ status: "", articleType: "", page: 1, limit: 25 });
  const [settings, setSettings] = useState(null);
  const [draft, setDraft] = useState({
    articleType: "song_review",
    topicHint: "",
    focusArtists: "",
    youtubeUrls: "",
    appleMusicUrls: "",
    internalPersonaSlug: "",
    targetSearch: "",
    targetArtist: "",
    targetSongId: "",
    targetAlbumId: "",
    claudeModel: "claude-sonnet-5",
  });
  const [catalogResults, setCatalogResults] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunError, setDryRunError] = useState("");
  const [selectedRun, setSelectedRun] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);

  const articleTypes = capabilities?.articleTypes || Object.keys(LABELS);
  const statuses = capabilities?.statuses || Object.keys(STATUS_LABELS);
  const personaSlugs = capabilities?.personaSlugs || [];
  const aiStatus = capabilities?.ai;
  const isTargeted = TARGETED_TYPES.includes(draft.articleType);
  const targetType = draft.articleType === "album_review" ? "album" : "song";
  const selectedTargetId = draft.articleType === "album_review" ? draft.targetAlbumId : draft.targetSongId;
  const canSubmitDraft = canCreate && (!isTargeted || Boolean(selectedTargetId));
  const validatedDraft = dryRunResult?.data?.validatedDraft;
  const qualityReport = dryRunResult?.data?.qualityReport;
  const legalReport = dryRunResult?.data?.evidenceBundle?.legal;

  const hydrateSettings = useCallback((value) => {
    if (!value) return;
    setSettings({
      ...value,
      focusArtistsText: (value.focusArtists || []).join("\n"),
      topicHintsText: (value.topicHints || []).join("\n"),
    });
  }, []);

  const loadRuns = useCallback(async (nextFilters = filters) => {
    const response = await allAPiServicesCall.listNewsroomRuns(nextFilters, getAuthConfig(), navigate);
    if (response?.data) {
      setRuns(response.data.items || []);
      setPagination(response.data.pagination || { page: 1, limit: 25, total: 0, pages: 0 });
    }
  }, [filters, navigate]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [capabilitiesResponse, controlResponse] = await Promise.all([
      allAPiServicesCall.getNewsroomCapabilities(getAuthConfig(), navigate),
      allAPiServicesCall.getNewsroomControl(getAuthConfig(), navigate),
    ]);
    setCapabilities(capabilitiesResponse?.data || null);
    setControl(controlResponse?.data || null);
    hydrateSettings(controlResponse?.data || null);
    await loadRuns(filters);
    setLoading(false);
  }, [filters, hydrateSettings, loadRuns, navigate]);

  useEffect(() => {
    loadAll();
  }, []);

  const totalWeight = useMemo(
    () => (settings?.articleTypeWeights || []).reduce((total, item) => total + Number(item.weight || 0), 0),
    [settings],
  );

  const updateWeight = (articleType, weight) => {
    setSettings((current) => {
      const next = (current.articleTypeWeights || []).filter((item) => item.articleType !== articleType);
      next.push({ articleType, weight: Number(weight) });
      return { ...current, articleTypeWeights: next };
    });
  };

  const buildDraftPayload = (action, includeDryRunOverrides = false) =>
    compactPayload({
      idempotencyKey: commandKey(action),
      articleType: draft.articleType,
      topicHint: draft.topicHint.trim(),
      focusArtists: splitLines(draft.focusArtists, 20),
      youtubeUrls: splitLines(draft.youtubeUrls, 25),
      appleMusicUrls: splitLines(draft.appleMusicUrls, 25),
      internalPersonaSlug: draft.internalPersonaSlug,
      targetSongId: draft.articleType === "song_review" ? draft.targetSongId : "",
      targetAlbumId: draft.articleType === "album_review" ? draft.targetAlbumId : "",
      claudeModel: includeDryRunOverrides ? draft.claudeModel.trim() : "",
    });

  const saveSettings = async () => {
    if (!settings || totalWeight !== 100) return;
    setSaving(true);
    const response = await allAPiServicesCall.updateNewsroomControl(
      {
        expectedVersion: settings.version,
        enabled: settings.enabled,
        intervalHours: Number(settings.intervalHours),
        articleTypeWeights: articleTypes.map((articleType) => ({
          articleType,
          weight: Number(settings.articleTypeWeights?.find((item) => item.articleType === articleType)?.weight || 0),
        })),
        focusArtists: splitLines(settings.focusArtistsText, 20),
        topicHints: splitLines(settings.topicHintsText, 30),
        personaSlugs: settings.personaSlugs || [],
      },
      getAuthConfig(),
      navigate,
    );
    if (response?.data) {
      setControl(response.data);
      hydrateSettings(response.data);
    }
    setSaving(false);
  };

  const runCatalogSearch = async () => {
    if (!draft.targetSearch.trim()) return;
    setCatalogLoading(true);
    try {
      const response = await searchCatalog(
        compactPayload({ type: targetType, q: draft.targetSearch.trim(), artist: draft.targetArtist.trim(), limit: 10 }),
        getAuthConfig(),
      );
      setCatalogResults(normalizeCatalogRows(response));
    } finally {
      setCatalogLoading(false);
    }
  };

  const selectTarget = (item) => {
    const id = catalogId(item);
    setSelectedTarget(item);
    setDraft((current) => ({
      ...current,
      targetSongId: current.articleType === "song_review" ? id : "",
      targetAlbumId: current.articleType === "album_review" ? id : "",
    }));
  };

  const startDraft = async () => {
    if (!canSubmitDraft) return;
    setDraftLoading(true);
    const response = await allAPiServicesCall.startNewsroomRun(buildDraftPayload("create"), getAuthConfig(), navigate);
    if (response) await loadRuns({ ...filters, page: 1 });
    setDraftLoading(false);
  };

  const runDryRun = async () => {
    if (!canSubmitDraft) return;
    setDryRunResult(null);
    setDryRunError("");
    setDryRunLoading(true);
    try {
      const response = await dryRunNewsroom(buildDraftPayload("dryrun", true), getAuthConfig());
      setDryRunResult(response);
      if (!response) setDryRunError("Dry-run failed without a response body.");
    } catch (error) {
      setDryRunError(error?.response?.data?.message || error?.message || "Dry-run request failed.");
      setDryRunResult(error?.response?.data || null);
    }
    setDryRunLoading(false);
  };

  const runCommand = async (run, action) => {
    if (action === "cancel") {
      const reason = window.prompt("Why should this run be cancelled?");
      if (!reason?.trim()) return;
      await allAPiServicesCall.cancelNewsroomRun(
        run.runId,
        { idempotencyKey: commandKey("cancel"), reason: reason.trim() },
        getAuthConfig(),
        navigate,
      );
    } else {
      await allAPiServicesCall.retryNewsroomRun(run.runId, { idempotencyKey: commandKey("retry") }, getAuthConfig(), navigate);
    }
    await loadRuns();
  };

  const openRun = async (run) => {
    setSelectedRun(run);
    setEvidence(null);
    if (!canReview) return;
    setEvidenceLoading(true);
    const response = await allAPiServicesCall.getNewsroomRunEvidence(run.runId, getAuthConfig(), navigate);
    setEvidence(response?.data?.evidenceBundle || null);
    setEvidenceLoading(false);
  };

  if (loading) {
    return (
      <CommonLayout>
        <div className="newsroom-loading"><Spinner animation="border" /><span>Loading newsroom controls...</span></div>
      </CommonLayout>
    );
  }

  return (
    <CommonLayout>
      <div className="newsroom-control-page">
        <div className="newsroom-hero">
          <div>
            <p className="newsroom-kicker">LyricsWeb Editorial Operations</p>
            <h1>Newsroom Control Center</h1>
            <p>Create targeted catalog-backed articles, dry-run every safety gate, and send only passing drafts into human review.</p>
          </div>
          <div className="newsroom-health">
            <Badge bg={control?.enabled ? "success" : "secondary"}>{control?.enabled ? "Automation enabled" : "Automation paused"}</Badge>
            <span>Configuration v{control?.version || 0}</span>
            <Button size="sm" variant="light" onClick={() => navigate("/add-news")}>Create manual article</Button>
          </div>
        </div>

        {capabilities?.pipeline && !Object.values(capabilities.pipeline).every(Boolean) && (
          <Alert variant="danger">One or more evidence or editorial safety gates are disabled on the server. Automated creation remains unavailable.</Alert>
        )}

        <Row className="g-4">
          <Col xl={7}>
            <Card className="newsroom-panel"><Card.Body>
              <div className="newsroom-section-heading">
                <div><h2>Automation settings</h2><p>Persistent, audited and applied by the worker.</p></div>
                <Form.Check type="switch" label={settings?.enabled ? "Enabled" : "Paused"} checked={settings?.enabled || false} disabled={!canConfigure} onChange={(event) => setSettings((current) => ({ ...current, enabled: event.target.checked }))} />
              </div>

              {aiStatus && (
                <div className="newsroom-ai-grid">
                  <div><span>Mode</span><strong>{aiStatus.mode}</strong></div>
                  <div><span>Research</span><strong>{aiStatus.researchModel}</strong></div>
                  <div><span>Writer</span><strong>{aiStatus.writerModel}</strong></div>
                  <div><span>Fact check</span><strong>{aiStatus.factCheckModel}</strong></div>
                </div>
              )}

              <Row className="g-3">
                <Col md={4}>
                  <Form.Label>Publishing interval</Form.Label>
                  <Form.Select value={settings?.intervalHours || 48} disabled={!canConfigure} onChange={(event) => setSettings((current) => ({ ...current, intervalHours: Number(event.target.value) }))}>
                    <option value={24}>Every 24 hours</option><option value={36}>Every 36 hours</option><option value={48}>Every 48 hours</option>
                  </Form.Select>
                </Col>
                <Col md={8}>
                  <Form.Label>Editorial mix (must total 100%)</Form.Label>
                  <div className={totalWeight === 100 ? "newsroom-weight-total valid" : "newsroom-weight-total invalid"}>Current total: {totalWeight}%</div>
                </Col>
                {articleTypes.map((articleType) => (
                  <Col md={6} key={articleType}>
                    <Form.Label>{LABELS[articleType] || articleType}</Form.Label>
                    <Form.Control type="number" min="0" max="100" disabled={!canConfigure} value={settings?.articleTypeWeights?.find((item) => item.articleType === articleType)?.weight || 0} onChange={(event) => updateWeight(articleType, event.target.value)} />
                  </Col>
                ))}
                <Col md={6}>
                  <Form.Label>Focus artists</Form.Label>
                  <Form.Control as="textarea" rows={5} disabled={!canConfigure} placeholder="One artist per line" value={settings?.focusArtistsText || ""} onChange={(event) => setSettings((current) => ({ ...current, focusArtistsText: event.target.value }))} />
                </Col>
                <Col md={6}>
                  <Form.Label>Preferred themes</Form.Label>
                  <Form.Control as="textarea" rows={5} disabled={!canConfigure} placeholder="Upcoming releases, overlooked music stories..." value={settings?.topicHintsText || ""} onChange={(event) => setSettings((current) => ({ ...current, topicHintsText: event.target.value }))} />
                </Col>
                <Col xs={12}>
                  <Form.Label>Internal writing personas</Form.Label>
                  <div className="newsroom-personas">
                    {personaSlugs.map((slug) => (
                      <Form.Check key={slug} inline type="checkbox" label={slug.replaceAll("-", " ")} disabled={!canConfigure} checked={(settings?.personaSlugs || []).includes(slug)} onChange={(event) => setSettings((current) => ({ ...current, personaSlugs: event.target.checked ? [...(current.personaSlugs || []), slug] : (current.personaSlugs || []).filter((item) => item !== slug) }))} />
                    ))}
                  </div>
                </Col>
              </Row>
              <Button className="mt-4" disabled={!canConfigure || saving || totalWeight !== 100} onClick={saveSettings}>{saving ? "Saving..." : "Save audited settings"}</Button>
            </Card.Body></Card>
          </Col>

          <Col xl={5}>
            <Card className="newsroom-panel"><Card.Body>
              <div className="newsroom-section-heading"><div><h2>Create targeted draft</h2><p>Dry-run first, then create a governed review draft.</p></div></div>
              <Alert variant="info" className="newsroom-compact-alert">Image URL, alt text, caption and rights metadata are not required here. They can be completed by the reviewer before approval.</Alert>

              <Form.Group className="mb-3">
                <Form.Label>Article type</Form.Label>
                <Form.Select value={draft.articleType} onChange={(event) => { const articleType = event.target.value; setSelectedTarget(null); setCatalogResults([]); setDraft((current) => ({ ...current, articleType, targetSongId: "", targetAlbumId: "" })); }}>
                  {articleTypes.map((type) => <option value={type} key={type}>{LABELS[type] || type}</option>)}
                </Form.Select>
              </Form.Group>

              {isTargeted && (
                <div className="newsroom-target-box">
                  <Form.Label>Target {targetType === "song" ? "song" : "album"} from catalog</Form.Label>
                  <InputGroup className="mb-2">
                    <Form.Control placeholder={targetType === "song" ? "Song title, e.g. The Fate of Ophelia" : "Album title"} value={draft.targetSearch} onChange={(event) => setDraft((current) => ({ ...current, targetSearch: event.target.value }))} onKeyDown={(event) => { if (event.key === "Enter") { event.preventDefault(); runCatalogSearch(); } }} />
                    <Button variant="outline-primary" disabled={catalogLoading || !draft.targetSearch.trim()} onClick={runCatalogSearch}>{catalogLoading ? "Searching..." : "Search"}</Button>
                  </InputGroup>
                  <Form.Control className="mb-2" placeholder="Optional artist filter" value={draft.targetArtist} onChange={(event) => setDraft((current) => ({ ...current, targetArtist: event.target.value }))} />
                  {catalogResults.length > 0 && (
                    <div className="newsroom-target-results">
                      {catalogResults.map((item) => {
                        const id = catalogId(item);
                        return (
                          <button type="button" key={id || JSON.stringify(item)} className={id && id === selectedTargetId ? "newsroom-target-option selected" : "newsroom-target-option"} onClick={() => selectTarget(item)}>
                            <strong>{catalogTitle(item)}</strong><span>{catalogArtist(item) || "Unknown artist"}</span><small>{id}</small>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <Form.Control className="mt-2" placeholder={targetType === "song" ? "Or paste targetSongId manually" : "Or paste targetAlbumId manually"} value={selectedTargetId} onChange={(event) => { const value = event.target.value.trim(); setSelectedTarget(null); setDraft((current) => ({ ...current, targetSongId: current.articleType === "song_review" ? value : "", targetAlbumId: current.articleType === "album_review" ? value : "" })); }} />
                  {selectedTarget && <div className="newsroom-selected-target">Selected: {catalogTitle(selectedTarget)}</div>}
                </div>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Topic or editorial direction</Form.Label>
                <Form.Control as="textarea" rows={5} value={draft.topicHint} onChange={(event) => setDraft((current) => ({ ...current, topicHint: event.target.value }))} />
              </Form.Group>
              <Row className="g-3">
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Writing persona</Form.Label><Form.Select value={draft.internalPersonaSlug} onChange={(event) => setDraft((current) => ({ ...current, internalPersonaSlug: event.target.value }))}><option value="">Automatic rotation</option>{personaSlugs.map((slug) => <option value={slug} key={slug}>{slug.replaceAll("-", " ")}</option>)}</Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Dry-run Claude override</Form.Label><Form.Control value={draft.claudeModel} onChange={(event) => setDraft((current) => ({ ...current, claudeModel: event.target.value }))} /></Form.Group></Col>
              </Row>
              <Form.Group className="mb-3"><Form.Label>Focus artists</Form.Label><Form.Control placeholder="Comma-separated" value={draft.focusArtists} onChange={(event) => setDraft((current) => ({ ...current, focusArtists: event.target.value }))} /></Form.Group>
              <Row className="g-3">
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Verified YouTube candidates</Form.Label><Form.Control as="textarea" rows={2} placeholder="One URL per line" value={draft.youtubeUrls} onChange={(event) => setDraft((current) => ({ ...current, youtubeUrls: event.target.value }))} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>Apple Music candidates</Form.Label><Form.Control as="textarea" rows={2} placeholder="One URL per line" value={draft.appleMusicUrls} onChange={(event) => setDraft((current) => ({ ...current, appleMusicUrls: event.target.value }))} /></Form.Group></Col>
              </Row>
              {!canSubmitDraft && <Alert variant="warning" className="newsroom-compact-alert">Select a catalog target before running a song or album review.</Alert>}
              <div className="newsroom-form-actions">
                <Button variant="outline-primary" disabled={!canSubmitDraft || dryRunLoading} onClick={runDryRun}>{dryRunLoading ? "Running dry-run..." : "Run dry-run"}</Button>
                <Button disabled={!canSubmitDraft || draftLoading} onClick={startDraft}>{draftLoading ? "Starting..." : "Create governed draft"}</Button>
              </div>
            </Card.Body></Card>
          </Col>
        </Row>

        {(dryRunResult || dryRunError) && (
          <Card className="newsroom-panel mt-4"><Card.Body>
            <div className="newsroom-section-heading"><div><h2>Dry-run result</h2><p>Server validation, legal flags and generated draft preview.</p></div>{qualityReport && <Badge bg={qualityReport.passed ? "success" : "danger"}>{qualityReport.passed ? "Passed" : "Blocked"}</Badge>}</div>
            {dryRunError && <Alert variant="danger">{dryRunError}</Alert>}
            {dryRunResult?.message && <Alert variant="secondary" className="newsroom-compact-alert">{dryRunResult.message}</Alert>}
            <div className="newsroom-result-grid">
              <div><span>Quality blockers</span><strong>{listText(qualityReport?.blockers)}</strong></div>
              <div><span>Legal risk</span><strong>{legalReport?.riskLevel || "Unknown"}</strong></div>
              <div><span>Legal flags</span><strong>{listText(legalReport?.flags)}</strong></div>
              <div><span>Words</span><strong>{validatedDraft?.wordCount || wordCount(validatedDraft?.description)}</strong></div>
              <div><span>Contains lyrics</span><strong>{String(Boolean(legalReport?.containsLyrics))}</strong></div>
              <div><span>Direct quotes</span><strong>{String(Boolean(legalReport?.containsDirectQuotes))}</strong></div>
            </div>
            {validatedDraft && <><div className="newsroom-draft-meta"><h3>{validatedDraft.title}</h3><p>{validatedDraft.metaDescription || validatedDraft.summary || "No summary returned."}</p></div><div className="newsroom-draft-preview" dangerouslySetInnerHTML={{ __html: validatedDraft.description || "" }} /></>}
          </Card.Body></Card>
        )}

        <Card className="newsroom-panel mt-4"><Card.Body>
          <div className="newsroom-section-heading"><div><h2>Runs and evidence</h2><p>Every stage, blocker and technical error is visible.</p></div><Button variant="outline-primary" onClick={() => loadRuns()}>Refresh</Button></div>
          <Row className="g-3 mb-3">
            <Col md={4}><Form.Select value={filters.status} onChange={(event) => { const next = { ...filters, status: event.target.value, page: 1 }; setFilters(next); loadRuns(next); }}><option value="">All statuses</option>{statuses.map((status) => <option value={status} key={status}>{STATUS_LABELS[status] || status}</option>)}</Form.Select></Col>
            <Col md={4}><Form.Select value={filters.articleType} onChange={(event) => { const next = { ...filters, articleType: event.target.value, page: 1 }; setFilters(next); loadRuns(next); }}><option value="">All article types</option>{articleTypes.map((type) => <option value={type} key={type}>{LABELS[type] || type}</option>)}</Form.Select></Col>
            <Col md={4} className="newsroom-results-count">{pagination.total} governed runs</Col>
          </Row>
          <div className="table-responsive"><Table hover className="newsroom-table"><thead><tr><th>Status</th><th>Type</th><th>Topic</th><th>Created</th><th>Attempts</th><th>Actions</th></tr></thead><tbody>
            {runs.map((run) => (
              <tr key={run.runId}>
                <td><Badge bg={run.status === "draft_created" ? "success" : run.status === "failed" || run.status === "blocked" ? "danger" : run.status === "cancelled" ? "secondary" : "primary"}>{STATUS_LABELS[run.status] || run.status}</Badge></td>
                <td>{LABELS[run.articleType] || run.articleType}</td><td>{run.topicHint || "Automatic selection"}</td><td>{new Date(run.createdAt).toLocaleString()}</td><td>{run.attempts}</td>
                <td className="newsroom-actions">
                  <Button size="sm" variant="outline-primary" onClick={() => openRun(run)}>Inspect</Button>
                  {run.draftNewsId && <Button size="sm" variant="outline-success" onClick={() => navigate(`/news-details/${run.draftNewsId}`)}>Review draft</Button>}
                  {canEdit && run.status === "failed" && <Button size="sm" variant="outline-warning" onClick={() => runCommand(run, "retry")}>Retry</Button>}
                  {canEdit && ["queued", "failed", "blocked"].includes(run.status) && <Button size="sm" variant="outline-danger" onClick={() => runCommand(run, "cancel")}>Cancel</Button>}
                </td>
              </tr>
            ))}
          </tbody></Table></div>
        </Card.Body></Card>

        <Modal show={Boolean(selectedRun)} onHide={() => setSelectedRun(null)} size="lg" centered>
          <Modal.Header closeButton><Modal.Title>Run inspection</Modal.Title></Modal.Header>
          <Modal.Body>{selectedRun && <>
            <dl className="newsroom-run-details"><dt>Run ID</dt><dd>{selectedRun.runId}</dd><dt>Status</dt><dd>{STATUS_LABELS[selectedRun.status] || selectedRun.status}</dd><dt>Model</dt><dd>{selectedRun.model}</dd><dt>Prompt</dt><dd>{selectedRun.promptVersion}</dd><dt>Last error</dt><dd>{selectedRun.lastErrorCode || "None"} {selectedRun.lastErrorMessage || ""}</dd><dt>Blockers</dt><dd>{selectedRun.blockers?.join(", ") || "None"}</dd></dl>
            {evidenceLoading && <Spinner animation="border" size="sm" />}
            {!canReview && <Alert variant="info">Evidence is restricted to independent reviewers.</Alert>}
            {evidence && <div className="newsroom-evidence"><h3>Verified sources</h3>{(evidence.sources || []).map((source) => <div className="newsroom-source" key={source.id || source.url}><strong>{source.title || source.publisher || "Source"}</strong><a href={source.url} target="_blank" rel="noreferrer">{source.url}</a></div>)}<h3>Claims</h3>{(evidence.claims || []).map((claim) => <div className="newsroom-claim" key={claim.id}><strong>{claim.id}</strong><span>{claim.statement || claim.claim}</span></div>)}</div>}
          </>}</Modal.Body>
        </Modal>
      </div>
    </CommonLayout>
  );
}

export default NewsroomControlCenter;
